<script>
import { apiSecond } from '@/boot/axios'
import { loadFile } from '@/helpers'

import Region from '@/components/components/Inputs/region'
import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'

import { aquaApiPrefix } from './aquaForms'
import AquaSelect from './AquaSelect.vue'

const STATUSES = ['draft', 'subject_signed', 'tir_signed', 'canceled_by_tir']

export default {
	name: 'AquaReportList',
	components: {
		Region,
		AqEmpty,
		AqPageHeader,
		AqStatus,
		AquaSelect,
	},
	props: {
		scope: {
			type: String,
			default: 'subject',
		},
	},
	data() {
		return {
			items: [],
			meta: { total: 0 },
			loading: false,
			denied: false,
			exporting: false,
			options: { itemsPerPage: 10, page: 1 },
			filters: {
				report_year: null,
				region_id: null,
				status: this.scope === 'tir' ? 'subject_signed' : null,
			},
		}
	},
	computed: {
		form() {
			return Number(this.$route.params.form)
		},
		apiPrefix() {
			return aquaApiPrefix(this.scope, this.form)
		},
		basePath() {
			const base = this.scope === 'go' ? 'reporting-analytics/aqua' : `reporting/${this.scope === 'tir' ? 'aqua-tir' : 'aqua'}`
			return `/${this.$i18n.locale}/account/${base}/${this.form}`
		},
		backPath() {
			return `/${this.$i18n.locale}/account/${this.scope === 'go' ? 'reporting-analytics' : 'reporting'}`
		},
		years() {
			const current = new Date().getFullYear()
			return Array.from({ length: 7 }, (_, index) => current - index)
		},
		yearOptions() {
			return [{ value: null, text: this.$t('aqua.ui.list.all_years') }, ...this.years.map((year) => ({ value: year, text: year }))]
		},
		statusOptions() {
			return [
				{ value: null, text: this.$t('aqua.ui.list.all_statuses') },
				...this.statuses.map((status) => ({ value: status, text: this.$t(`aqua.status.${status}`) })),
			]
		},
		statuses() {
			return this.scope === 'subject' ? STATUSES : STATUSES.filter((status) => status !== 'draft')
		},
		hasFilters() {
			return Boolean(this.filters.report_year || this.filters.region_id || this.filters.status)
		},
		headers() {
			const headers = [
				{ text: '№', value: 'id', width: '72px', sortable: false },
				{ text: this.$t('aqua.report_year'), value: 'report_year', sortable: false, width: '130px' },
				{ text: this.$t('inputs.region'), value: 'region', sortable: false },
			]
			if (this.scope !== 'subject') headers.push({ text: this.$t('aqua.subject'), value: 'subject', sortable: false })
			headers.push(
				{ text: this.$t('headers.status'), value: 'status', sortable: false },
				{ text: this.$t('aqua.submitted_at'), value: 'submitted_at', sortable: false, width: '150px' },
				{ text: '', value: 'action', sortable: false, width: '112px', align: 'end' },
			)
			return headers
		},
		canCreate() {
			return this.scope === 'subject' && this.$store.getters.userPermission(['change_reporting-aqua'])
		},
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.options = { ...this.options, page: 1 }
				this.fetch()
			},
		},
		'$route.params.form'() {
			this.fetch()
		},
	},
	methods: {
		params() {
			const params = { page: this.options.page, per_page: this.options.itemsPerPage }
			Object.keys(this.filters).forEach((key) => {
				if (this.filters[key]) params[key] = this.filters[key]
			})
			return params
		},
		resetFilters() {
			this.filters = { report_year: null, region_id: null, status: null }
		},
		onOptions(options) {
			const changed = options.page !== this.options.page || options.itemsPerPage !== this.options.itemsPerPage
			this.options = options
			if (changed || !this.items.length) this.fetch()
		},
		openReport(item) {
			this.$router.push(`${this.basePath}/${item.id}`)
		},
		async fetch() {
			this.loading = true
			try {
				const { data } = await apiSecond.get(this.apiPrefix, { params: this.params() })
				this.items = data.data
				this.meta = data.meta
				this.denied = false
			} catch (error) {
				this.denied = error?.response?.status === 403
				if (!this.denied) this.handleError(error)
			} finally {
				this.loading = false
			}
		},
		async exportExcel() {
			if (this.exporting) return
			this.exporting = true
			try {
				const params = this.params()
				delete params.page
				delete params.per_page
				const response = await apiSecond.get(`${this.apiPrefix}/export`, { params, responseType: 'blob' })
				loadFile(response.data, response.headers['content-type'], `${this.form}-aqua`)
			} catch (error) {
				this.handleError(error)
			} finally {
				this.exporting = false
			}
		},
		async downloadPdf(id) {
			try {
				const response = await apiSecond.get(`${this.apiPrefix}/${id}/pdf`, { responseType: 'blob' })
				loadFile(response.data, 'application/pdf', `${this.form}-aqua-${id}`)
			} catch (error) {
				this.handleError(error)
			}
		},
		handleError(error) {
			if (error?.response?.status === 401) {
				this.$router.push(`/${this.$i18n.locale}/login`)
				return
			}
			this.$toast.error(error?.response?.data?.message || this.$t('system_message.500'))
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
}
</script>

<template>
	<div class="wrap__content aq-page">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="[scope === 'go' ? 'ReportingAnalyticManagement' : 'ReportingManagement']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="$t(`aqua.forms.${form}`)"
								:eyebrow="`${form}-аква`"
								:back="backPath"
								:back-label="$t('aqua.ui.list.back')"
							>
								<template #meta>
									<span class="aq-badge aq-badge--neutral">{{ $t('aqua.ui.list.total', { count: meta.total || 0 }) }}</span>
								</template>
								<template #actions>
									<router-link v-if="scope !== 'subject'" :to="`${basePath}/summary`" class="aq-btn">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
										</svg>
										{{ $t('aqua.ui.list.summary') }}
									</router-link>
									<button v-if="scope === 'go'" type="button" class="aq-btn" :disabled="exporting" @click="exportExcel">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
										</svg>
										Excel
									</button>
									<router-link v-if="canCreate && !denied" :to="`${basePath}/create`" class="aq-btn aq-btn--primary">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
											<path d="M12 5v14M5 12h14" />
										</svg>
										{{ $t('button.add') }}
									</router-link>
								</template>
							</AqPageHeader>

							<section v-if="denied" class="aq-card li-card">
								<AqEmpty tone="danger" :title="$t('aqua.ui.list.denied_title')" :text="$t('aqua.ui.list.denied_text')">
									<router-link :to="backPath" class="aq-btn aq-btn--primary">{{ $t('aqua.ui.list.back') }}</router-link>
								</AqEmpty>
							</section>

							<section v-else class="aq-card li-card">
								<div class="li-filters">
									<AquaSelect v-model="filters.report_year" :label="$t('aqua.ui.list.year')" :options="yearOptions" />
									<div v-if="scope !== 'subject'" class="li-filter">
										<span class="li-filter__label">{{ $t('aqua.ui.list.region') }}</span>
										<region
											:modelValue="filters.region_id"
											:placeholder="$t('inputs.region')"
											@update:modelValue="filters.region_id = $event"
										></region>
									</div>
									<AquaSelect v-model="filters.status" :label="$t('aqua.ui.list.status')" :options="statusOptions" />
									<button v-if="hasFilters" type="button" class="aq-btn aq-btn--ghost li-reset" @click="resetFilters">
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
											<path d="M6 6l12 12M18 6L6 18" />
										</svg>
										{{ $t('aqua.ui.list.reset') }}
									</button>
								</div>

								<v-data-table
									class="li-dt"
									:headers="headers"
									:items="items"
									:loading="loading"
									loader-height="3"
									:options="options"
									:server-items-length="meta.total"
									:footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
									@update:options="onOptions"
									@click:row="openReport"
								>
									<template v-slot:item.id="{ item }">
										<span class="li-id">{{ item.id }}</span>
									</template>
									<template v-slot:item.report_year="{ item }">
										<strong>{{ item.report_year }}</strong>
									</template>
									<template v-slot:item.region="{ item }">{{ item.region ? item.region.title : '—' }}</template>
									<template v-slot:item.subject="{ item }">
										<div class="li-subject">
											<span class="li-subject__name">{{ item.subject.name }}</span>
											<span class="li-subject__meta">{{ item.subject.iin_bin }}</span>
										</div>
									</template>
									<template v-slot:item.status="{ item }">
										<div class="li-status">
											<AqStatus :value="item.status.value" :label="$t(`aqua.status.${item.status.value}`)" />
											<AqStatus v-if="item.is_late" tone="danger" :label="$t('aqua.late')" />
											<AqStatus v-if="scope !== 'subject' && item.is_tir_late" tone="warning" :label="$t('aqua.compliance.tir_late_short')" />
										</div>
									</template>
									<template v-slot:item.submitted_at="{ item }">
										<span class="li-date" :class="{ 'li-muted': !item.submitted_at }">{{ item.submitted_at || '—' }}</span>
									</template>
									<template v-slot:item.action="{ item }">
										<div class="li-actions">
											<router-link
												:to="`${basePath}/${item.id}`"
												class="li-icon"
												:aria-label="$t('aqua.ui.list.open')"
												:title="$t('aqua.ui.list.open')"
												@click.native.stop
											>
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true">
													<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
													<circle cx="12" cy="12" r="3" />
												</svg>
											</router-link>
											<button
												type="button"
												class="li-icon"
												:aria-label="$t('aqua.ui.list.pdf')"
												:title="$t('aqua.ui.list.pdf')"
												@click.stop="downloadPdf(item.id)"
											>
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true">
													<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
												</svg>
											</button>
										</div>
									</template>
									<template v-slot:no-data>
										<AqEmpty
											:title="$t('aqua.ui.list.empty_title')"
											:text="hasFilters ? $t('aqua.ui.list.empty_filtered') : canCreate ? $t('aqua.ui.list.empty_subject') : ''"
										>
											<button v-if="hasFilters" type="button" class="aq-btn" @click="resetFilters">{{ $t('aqua.ui.list.reset') }}</button>
											<router-link v-else-if="canCreate" :to="`${basePath}/create`" class="aq-btn aq-btn--primary">
												{{ $t('button.add') }}
											</router-link>
										</AqEmpty>
									</template>
								</v-data-table>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.li-card {
	overflow: hidden;
}
.li-filters {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 14px 16px;
	padding: 18px 24px;
	border-bottom: 1px solid var(--aq-border);
}
.li-filters > .aqua-select {
	width: 200px;
}
.li-filter {
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 260px;
}
.li-filter__label,
.li-filters ::v-deep .aqua-select__label {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.li-filters ::v-deep .aqua-select {
	gap: 6px;
}
.li-filters ::v-deep .aqua-select__control {
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.li-filters ::v-deep .aqua-select__control:hover {
	border-color: var(--aq-primary);
}
.li-filters ::v-deep .input-wrapper {
	margin: 0;
}
.li-filters ::v-deep .multiselect,
.li-filters ::v-deep .multiselect__tags {
	min-height: 44px;
}
.li-filters ::v-deep .multiselect__tags {
	height: 44px;
}
.li-filters ::v-deep .multiselect__placeholder,
.li-filters ::v-deep .multiselect__single {
	margin-bottom: 0;
	padding-top: 0;
}
.li-filters ::v-deep .input-body .multiselect__tags {
	padding-top: 11px;
	padding-left: 14px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
}
.li-filters ::v-deep .input-body .multiselect__tags:hover {
	border-color: var(--aq-primary) !important;
}
.li-filters ::v-deep .multiselect__select {
	height: 42px;
}
.li-filters ::v-deep .multiselect__single,
.li-filters ::v-deep .multiselect__placeholder {
	overflow: hidden;
	font-size: 15px;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.li-reset {
	min-height: 44px;
}
.li-id {
	color: var(--aq-muted);
	font-size: 13px;
}
.li-date {
	white-space: nowrap;
}
.li-muted {
	color: var(--aq-muted);
}
.li-subject {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 4px 0;
}
.li-subject__name {
	font-weight: 600;
}
.li-subject__meta {
	color: var(--aq-muted);
	font-size: 12px;
}
.li-status {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.li-actions {
	display: flex;
	justify-content: flex-end;
	gap: 4px;
}
.li-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 10px;
	color: var(--aq-ink-2);
	transition: background 0.2s var(--aq-ease), color 0.2s var(--aq-ease);
}
.li-icon:hover {
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
}
.li-icon:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.li-dt ::v-deep table {
	font-variant-numeric: tabular-nums;
}
.li-dt ::v-deep thead th {
	height: 48px;
	background: #f8fafd;
	color: var(--aq-ink-2) !important;
	font-size: 12px !important;
	font-weight: 600;
	white-space: nowrap;
}
.li-dt ::v-deep tbody td {
	height: 60px;
	padding: 8px 16px !important;
	border-bottom: 1px solid var(--aq-border) !important;
	color: var(--aq-ink);
	font-size: 14px !important;
}
.li-dt ::v-deep thead th:first-child,
.li-dt ::v-deep tbody td:first-child {
	padding-left: 24px !important;
}
.li-dt ::v-deep tbody tr:not(.v-data-table__empty-wrapper) {
	cursor: pointer;
	transition: background 0.15s var(--aq-ease);
}
.li-dt ::v-deep tbody tr:hover:not(.v-data-table__empty-wrapper) {
	background: #f5f9ff !important;
}
.li-dt ::v-deep .v-data-table__empty-wrapper td {
	height: auto;
}
.li-dt ::v-deep .v-data-footer {
	padding: 6px 16px;
	border-top: 1px solid var(--aq-border) !important;
	color: var(--aq-muted);
	font-size: 13px;
}
</style>
